import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { FormlyFieldSelectProps } from '@ngx-formly/core/select';
import { Select } from 'primeng/select';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';
import { TranslatePipe } from '@ngx-translate/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { DrawerModule } from 'primeng/drawer';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { AutoFocusModule } from 'primeng/autofocus';

export interface SelectFieldProps
  extends FormlyFieldProps,
    FormlyFieldSelectProps {
  appendTo?: any;
  filter?: boolean;
  optionLabel?: string;
  optionValue?: string;
  group?: boolean;
  optionGroupLabel?: string;
  optionGroupChildren?: string;
  showClear?: boolean;
  nestedValue?: boolean;
  filterMatchMode?: typeof Select.prototype.filterMatchMode;
  translateLabel?: string;
  optionDescription?: string;
  dropdownIcon?: string;
  styleClass?: string;
}

@Component({
  selector: 'select-field',
  standalone: true,
  imports: [
    CommonModule,
    ...FORM_IMPORTS,
    Select,
    TranslatePipe,
    IconFieldModule,
    InputIconModule,
    InputText,
    DrawerModule,
    TableModule,
    AutoFocusModule,
  ],
  template: `
    <p-select
      #dropdown
      [class]="props.styleClass ?? ''"
      (onClick)="handleDropdownClick($event, dropdown)"
      [panelStyleClass]="isMobile ? 'hide-dropdown-panel' : ''"
      [dropdownIcon]="props.dropdownIcon ?? 'fa-solid fa-caret-down text-xl'"
      [placeholder]="props.placeholder"
      [formControl]="formControl"
      [formlyAttributes]="field"
      [options]="$any(props.options)"
      autocomplete="false"
      [showClear]="props.showClear ?? false"
      [required]="props.required ?? false"
      [filter]="props.filter"
      (onChange)="props.change && props.change(field, $event)"
      [virtualScroll]="true"
      [virtualScrollItemSize]="43"
      fluid="true"
      [filterMatchMode]="props.filterMatchMode ?? 'contains'"
      [optionLabel]="props.optionLabel ?? 'label'"
      [optionValue]="
        props.nestedValue
          ? undefined
          : props.optionValue
            ? props.optionValue
            : undefined
      "
      optionDisabled="disabled"
      [group]="props.group"
      [optionGroupLabel]="
        props.optionGroupLabel ? props.optionGroupLabel : 'label'
      "
      [optionGroupChildren]="
        props.optionGroupChildren ? props.optionGroupChildren : 'items'
      "
      [appendTo]="props.appendTo ?? 'body'"
    >
      <ng-template #selectedItem let-selected>
        @if (props.translateLabel) {
          {{
            props.translateLabel +
              '.' +
              (props.optionValue ? selected[props.optionValue] : selected)
              | translate
          }}
        } @else {
          {{ selected[props.optionLabel ?? 'label'] }}
        }
      </ng-template>
      <ng-template let-el #item>
        <div>
          @if (props.translateLabel) {
            {{
              props.translateLabel +
                '.' +
                (props.optionValue ? el[props.optionValue] : el) | translate
            }}
          } @else {
            {{ el[props.optionLabel ?? 'label'] }}
          }

          @if (props.optionDescription) {
            <div class="text-xs italic">
              {{ el[props.optionDescription] }}
            </div>
          }
        </div>
      </ng-template>
    </p-select>

    <p-drawer
      [(visible)]="drawer"
      [position]="'bottom'"
      styleClass="h-auto min-h-[655px] pt-22"
      baseZIndex="1104"
      [maskStyle]="$any('z-index:1103')"
      (onShow)="onShow($event)"
    >
      <ng-template #closeicon>
        <i class="fa-solid fa-caret-up text-xl"></i>
      </ng-template>
      <ng-template #header>
        <div class="flex items-center gap-2">
          <span class="font-bold">{{
            props.label != '' ? props.label : props.placeholder
          }}</span>
        </div>
      </ng-template>
      <p-table
        #dt2
        scrollHeight="500px"
        [virtualScroll]="true"
        [virtualScrollItemSize]="46"
        [value]="$any(props.options)"
        selectionMode="single"
        [(selection)]="selected"
        [globalFilterFields]="[props.optionLabel ? props.optionLabel : 'label']"
        (selectionChange)="selectionChange($event)"
      >
        @if (props.filter) {
          <ng-template #caption>
            <div class="flex">
              <p-iconfield iconPosition="left" class="w-full">
                <input
                  #search
                  pInputText
                  [pAutoFocus]="true"
                  type="text"
                  (input)="
                    dt2.filterGlobal($any($event.target).value, 'contains')
                  "
                /><p-inputicon>
                  <i class="fas fa-search"></i>
                </p-inputicon>
              </p-iconfield>
            </div>
          </ng-template>
        }

        <ng-template #body let-el>
          <tr [pSelectableRow]="el">
            <td>
              @if (props.translateLabel) {
                {{
                  props.translateLabel +
                    '.' +
                    (props.optionValue ? el[props.optionValue] : el) | translate
                }}
              } @else {
                {{ el[props.optionLabel ? props.optionLabel : 'label'] }}
              }
            </td>
          </tr>
        </ng-template>
        <ng-template #emptyMessage>
          {{ 'primeng.emptyMessage' | translate }}
        </ng-template>
      </p-table>
    </p-drawer>
  `,
  styles: `
    :host ::ng-deep {
      p-select {
        @apply justify-between;
        @apply flex-wrap;
      }
      p-overlay {
        @apply w-full;
      }
      .p-select-label {
        @apply flex-none;
        width: calc(100% - var(--p-select-dropdown-width));
      }
    }
  `,
})
export class SelectFieldType extends FieldType<
  FieldTypeConfig<SelectFieldProps>
> {
  isMobile: boolean = false;
  @ViewChild('dt2') dt2!: any;
  @ViewChild('search', { static: false }) search?: ElementRef<HTMLInputElement>;

  ngOnInit() {
    this.checkIfMobile();
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkIfMobile();
  }

  private checkIfMobile() {
    this.isMobile = window.innerWidth <= 768;
  }
  drawer: boolean = false;

  selected: any;

  handleDropdownClick(event: MouseEvent, dropdown: any) {
    if (this.isMobile) {
      event.preventDefault();
      event.stopPropagation();
      this.drawer = !this.drawer;
    }
  }

  selectionChange(event: any) {
    if (event) {
      if (this.props.optionValue && !this.props.nestedValue) {
        this.formControl.setValue(event[this.props.optionValue]);
      } else {
        this.formControl.setValue(event);
      }
    } else {
      if (this.props.showClear) this.formControl.setValue(undefined);
    }
    this.drawer = false;
  }

  onShow(event: any) {
    const options = this.props.options as any[];

    if (this.formControl.value) {
      const selectedIndex = options.findIndex((el) =>
        this.props.optionValue
          ? el[this.props.optionValue] == this.formControl.value
          : el == this.formControl.value,
      );
      this.selected = options[selectedIndex];
      setTimeout(() => {
        this.search?.nativeElement.focus();

        if (
          this.dt2?.scroller &&
          typeof this.dt2.scroller.scrollToIndex === 'function'
        ) {
          this.dt2.scroller.scrollToIndex(selectedIndex);
        } else {
          console.warn(
            'Scroller non disponibile o metodo scrollToIndex non trovato',
          );
        }
      });
    }
  }
}
